// services/dashboard-data.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { DashboardTab, CropType } from '../models/crop.interface';

@Injectable({ providedIn: 'root' })
export class DashboardDataService {
  private baseEndpoint = '/tools/agrorendement';

  private cropPathMap = {
    [CropType.BLE]: 'ble',
    [CropType.COLZA]: 'colza',
    [CropType.ORGE]: 'orge',
  };

  constructor(private http: HttpClient) {}

  private buildCropChartUrl(type: CropType, chartName: string, force?: boolean): string {
    const cropPath = this.cropPathMap[type];
    const refresh = force ? '?refresh=1' : '';
    return `${this.baseEndpoint}/${cropPath}/dashboard/${chartName}${refresh}`;
  }

  private unwrap(res: any): { data: any; meta: any } {
    const j: any = res;
    // { status, data: { data: <payload>, meta: <meta> } }
    const outer = j && j.data;
    const data = (outer && outer.data) || {};
    const meta = (outer && outer.meta) || null;
    return { data, meta };
  }

  private fetchChartData(type: CropType, chartName: string, force?: boolean): Observable<{ data: any; meta: any }> {
    const url = this.buildCropChartUrl(type, chartName, force);
    return this.http.get<any>(url).pipe(
      map((res: any) => this.unwrap(res)),
      catchError((err: any) => {
        try {
          console.error(`Error fetching '${chartName}' chart data for cropType=${type}`, err);
          const j = err && err.error ? err.error : null;
          return throwError(() => (j && j.error) || 'Server error');
        } catch (e) {
          return throwError(() => 'Server error');
        }
      }),
    );
  }

  getBarChart(type: CropType, force?: boolean) {
    return this.fetchChartData(type, 'bar', force);
  }
  getDoughnutData(type: CropType, force?: boolean) {
    return this.fetchChartData(type, 'doughnut', force);
  }
  getDoughnutAverage(type: CropType, force?: boolean) {
    return this.fetchChartData(type, 'doughnut-average', force);
  }
  getGaugeData(type: CropType, force?: boolean) {
    return this.fetchChartData(type, 'gauge', force);
  }
  getGroupedBarData(type: CropType, force?: boolean) {
    return this.fetchChartData(type, 'grouped-bar', force);
  }

  /**
   * The five charts of all three crops in a single request. Used by the update button; the server
   * computes the fifteen in parallel.
   */
  getAllDashboards(force?: boolean): Observable<{ data: any; meta: any }> {
    const url = `${this.baseEndpoint}/common/dashboard${force ? '?refresh=1' : ''}`;
    return this.http.get<any>(url).pipe(
      map((res: any) => this.unwrap(res)),
      catchError((err: any) => {
        console.error('Error fetching aggregated dashboard data', err);
        const j = err && err.error ? err.error : null;
        return throwError(() => (j && j.error) || 'Server error');
      }),
    );
  }

  getTabs(): DashboardTab[] {
    return [
      { type: CropType.BLE, label: 'Blé', data: null },
      { type: CropType.COLZA, label: 'Colza', data: null },
      { type: CropType.ORGE, label: 'Orge', data: null },
    ];
  }
}
